import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import ReactMarkdown from 'react-markdown';

import { summarizeTranscript } from './api/summarize';
import FirebaseLogin from './components/FirebaseLogin';
import LandingHeaderAnimation from './components/LandingHeaderAnimation';
import UserMenu from './components/UserMenu';
import ChatMenu from './components/ChatMenu';
import { useAuth } from './hooks/useAuth';
import { useDatabase } from './hooks/useDatabase';
import { useToast } from './hooks/useToast';

const SpeechToTextRecognition = () => {
  const {
    transcript,
    interimTranscript,
    finalTranscript,
    resetTranscript,
    listening,
    browserSupportsSpeechRecognition,
    isMicrophoneAvailable
  } = useSpeechRecognition();

  const [summary, setSummary] = useState('');
  const [showLogin, setShowLogin] = useState(false);
  const [getLoading, setLoading] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [activated, setActivated] = useState(false);
  const intervalRef = useRef(null);
  const containerRef = useRef(null);

  const { user, isAuthenticated, loading } = useAuth();
  const { saveUserSummary } = useDatabase();
  const { showSuccess, showError, showWarning, showInfo } = useToast();

  useEffect(() => {
    if (browserSupportsSpeechRecognition && isMicrophoneAvailable) {
      // auto-start
      // SpeechRecognition.startListening({ continuous: true, language: 'en-US' });
    }

    if (containerRef.current) {
        containerRef.current.scrollLeft = containerRef.current.scrollWidth;
    }

    if (isRunning) {
        intervalRef.current = setInterval(() => {
            setSeconds((prev) => prev + 1);
        }, 1000);
    } else {
        clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);

  }, [browserSupportsSpeechRecognition, isMicrophoneAvailable, isRunning]);

  const handleSummarize = async () => {
    try {
      setLoading(true);
      const result = await summarizeTranscript(transcript);
      console.log(result);
      setSummary(result.slice(11, -3));
    // } catch (error) {
    //   alert('Summarization failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSummaryUpload = async () => {
    try {
      await saveUserSummary(summary);
      showInfo('Summary saved');
    } catch (e) {
      console.log("error: ", e);
    }
  }

  const formatTime = (totalSeconds) => {
    const hrs = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
    const mins = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
    const secs = String(totalSeconds % 60).padStart(2, '0');
    return `${hrs}:${mins}:${secs}`;
  };

  const startListening = async () => {
    console.log(isAuthenticated);
    if(isAuthenticated) {
      await SpeechRecognition.startListening({ 
        continuous: true, 
        language: 'en-US',
        interimResults: true 
      });
      setIsRunning(true);
      setActivated(true);
    } else {
      setShowLogin(true);
    }
  };

  const stopListening = async () => {
    await SpeechRecognition.stopListening();
    setIsRunning(false);
  };

  const resetAll = () => {
    resetTranscript();
    setIsRunning(false);
    setSeconds(0);
    if (listening) {
      SpeechRecognition.stopListening();
    }
  };

  const EndMeeting = () => {
    resetTranscript();
    setIsRunning(false);
    setSeconds(0);
    if (listening) {
      SpeechRecognition.stopListening();
    }
    handleSummarize();
    showInfo('Summarizing');
  };

  // Browser support check
  if (!browserSupportsSpeechRecognition) {
    return (
      <div class="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center p-4">
          <h2 class="text-2xl font-bold text-gray-800 mb-2">Browser Not Supported</h2>
      </div>
    );
  }

  // Microphone permission check
  if (!isMicrophoneAvailable) {
    return (
      <div class="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-100 flex items-center justify-center p-4">
        <div class="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div class="text-yellow-500 text-6xl mb-4">🎤</div>
          <h2 class="text-2xl font-bold text-gray-800 mb-2">Microphone Access Required</h2>
          <p class="text-gray-600 mb-4">
            Please allow microphone access to use speech recognition.
          </p>
          <button
            onClick={() => window.location.reload()}
            class="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div class={`min-h-screen ${user ? '': (showLogin ? 'bg-gray-300' : '')} bg-[radial-gradient(circle,_rgba(0,0,0,0.26)_1px,_transparent_1px)] bg-[length:35px_35px] p-4`}>
      <div class="max-w-4xl mx-auto">
        { user ? <></> : showLogin && <FirebaseLogin /> }
        { user && <> <UserMenu /> <ChatMenu /> </> }

        {/* Controls */}
        {/* { user ? ( */}
          <>
            <div
              class={`transition-all duration-700 ease-in-out fixed ${
                user && activated ? 'bottom-10 right-10' : 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2'
              } z-20`}
            >
              {!activated && <div class="font-semibold font-roboto text-5xl text-gray-800 ml-18 m-12"><LandingHeaderAnimation /></div> }
              <div class={`flex flex-wrap gap-4 items-center ${activated ? 'justify-end' : 'justify-center'}`}>
                <button
                  onClick={listening ? stopListening : startListening}
                  class={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform ${
                    listening
                      ? 'bg-gray-500 hover:bg-gray-600 text-white animate-pulse'
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                >
                  {listening ? 'Pause' : 'Start'}
                </button>

                {/* Extra buttons visible only after activation */}
                {user && activated && (
                  <>
                    <button
                      onClick={resetAll}
                      disabled={!transcript && !listening}
                      class="flex items-center gap-2 px-6 py-3 rounded-full font-semibold bg-orange-500 hover:bg-orange-600 text-white transition-all duration-300 shadow-lg hover:shadow-xl transform disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      Reset
                    </button>
                    
                    <button
                      onClick={EndMeeting}
                      disabled={!transcript && !listening}
                      class="flex items-center gap-2 px-8 py-3 rounded-full font-semibold bg-red-500 hover:bg-red-600 text-white transition-all duration-300 shadow-lg hover:shadow-xl transform disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      End
                    </button>

                    <div class="flex justify-center gap-6">
                      <div class={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                        listening 
                          ? 'bg-green-100 text-green-800 border border-green-200' 
                          : 'bg-gray-100 text-gray-600 border border-gray-200'
                      }`}>
                        <div class={`w-2 h-2 rounded-full ${listening ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
                        <div class="text-md font-mono">{formatTime(seconds)}</div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {summary && (
              <div class="prose mt-24 lg:mt-0 lg:prose-xl max-w-full bg-gray-50 rounded-2xl shadow-xl font-roboto p-6 min-h-[400px] overflow-hidden relative">
                  {/* <div class="flex justify-end w-full">   */}
                    <button
                      onClick={handleSummaryUpload}
                      class="absolute top-4 right-4 flex items-center gap-2 px-2 py-2 border border-gray-300 rounded-lg font-regular bg-gray-100 hover:bg-gray-300 text-gray-800 text-sm transition-all duration-300 transform disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                      >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M9.25 9.05a8 8 0 0 0 5.5 0" stroke="#777777" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M16.82 2H7.18C5.05 2 3.32 3.74 3.32 5.86v14.09c0 1.8 1.29 2.56 2.87 1.69l4.88-2.71c.52-.29 1.36-.29 1.87 0l4.88 2.71c1.58.88 2.87.12 2.87-1.69V5.86C20.68 3.74 18.95 2 16.82 2Z" stroke="#777777" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M16.82 2H7.18C5.05 2 3.32 3.74 3.32 5.86v14.09c0 1.8 1.29 2.56 2.87 1.69l4.88-2.71c.52-.29 1.36-.29 1.87 0l4.88 2.71c1.58.88 2.87.12 2.87-1.69V5.86C20.68 3.74 18.95 2 16.82 2Z" stroke="#777777" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>
                    </button>
                  {/* </div> */}
                  <ReactMarkdown
                    components={{
                      h1: ({ node, ...props }) => <h1 className="text-4xl font-bold mt-6 mb-4" {...props} />,
                      h2: ({ node, ...props }) => <h2 className="text-2xl text-gray-900 font-bold mt-5 mb-3" {...props} />,
                      h3: ({ node, ...props }) => <h3 className="text-lg text-gray-800 font-semibold mt-4 mb-2" {...props} />,
                      h4: ({ node, ...props }) => <h3 className="text-lg text-gray-700 font-semibold mt-4 mb-2" {...props} />,
                      p: ({ node, ...props }) => <p className="mb-4 text-gray-700" {...props} />,
                      li: ({ node, ...props }) => <li className="ml-6 list-disc text-gray-700" {...props} />,
                      strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
                      br: () => <br className="my-2" />,
                    }}
                    class="prose lg:prose-xl max-w-full font-roboto"
                  >{summary}</ReactMarkdown>
              </div>
            )}

            {/* Transcript Display */}
            {user && activated ? 
              <>
                <div
                  class={`top-1/2 left-1/2 transition-opacity duration-700 ease-in-out mt-20 ${
                    activated ? 'opacity-100' : 'opacity-0 pointer-events-none'
                  }`}
                >
                  <div class="bg-white rounded-2xl shadow-xl p-6 min-h-[400px] overflow-hidden relative">
                  
                      <div class="relative min-h-[340px] flex items-center justify-center">
                          <div class="absolute left-0 top-0 h-full w-12 lg:w-24 z-10 pointer-events-none bg-gradient-to-r from-white via-white to-transparent"></div>
                          {/* <div class="absolute right-0 top-0 h-full w-24 z-10 pointer-events-none bg-gradient-to-l from-white via-white to-transparent"></div> */}

                          <div 
                              ref={containerRef}
                              class="w-full overflow-x-auto whitespace-nowrap px-8 scrollbar-hide scroll-smooth"        
                          >
                          {transcript || interimTranscript ? (
                          <div class="inline-block text-4xl font-roboto font-semibold text-gray-600 transition-all duration-300">
                              {/* Final transcript */}
                              <span class="text-gray-600">
                                  {transcript}
                              </span>
                              
                              {/* Interim transcript (what's currently being spoken) */}
                              {interimTranscript && (
                              <span class="text-gray-300 px-1 rounded ml-1">
                                  {interimTranscript}
                              </span>
                              )}
                          </div>
                          ) : (
                          <div class="text-center text-xl font-roboto text-gray-400 py-20">
                              Let the conversations begin
                          </div>
                          )}
                          </div>
                      </div>
                  </div>
              </div>
              </> : <></>
            }
          </>
          {/* ) : <></> } */}
      </div>
    </div>
  );
};

export default SpeechToTextRecognition;